import { colors } from '@diversioteam/diversio-ds'

let cachedBaseHref: string | null = null
let originalBaseHref: string | null = null
let cachedBaseImage: HTMLImageElement | null = null
let loadingPromise: Promise<HTMLImageElement> | null = null
let lastDisplayedCount: number | null = null
let sharedCanvas: HTMLCanvasElement | null = null
let sharedCtx: CanvasRenderingContext2D | null = null

const ensureFaviconLink = (): HTMLLinkElement => {
    let link = document.querySelector("link[rel='icon'][data-ds-favicon]") as HTMLLinkElement | null
    if (!link) {
        link = document.createElement('link')
        link.rel = 'icon'
        link.type = 'image/png'
        link.setAttribute('data-ds-favicon', 'true')
        document.head.appendChild(link)
    }
    return link
}

// Some browsers cache and don't repaint the tab icon on href mutation.
// Replacing the node forces a repaint reliably even when hidden.
const applyFaviconHref = (href: string) => {
    // Remove all existing favicon links to prevent browsers from keeping the first one
    document.querySelectorAll("link[rel*='icon']").forEach((n) => n.remove())

    const makeLink = (rel: string) => {
        const link = document.createElement('link')
        link.rel = rel
        const isSvg = href.endsWith('.svg') || href.startsWith('data:image/svg')
        link.type = isSvg ? 'image/svg+xml' : 'image/png'
        link.setAttribute('sizes', 'any')
        link.setAttribute('data-ds-favicon', 'true')
        // Use a short cache buster for safety on certain engines when not data URL
        link.href = href.startsWith('data:image') ? href : `${href}${href.includes('?') ? '&' : '?'}v=${Date.now()}`
        document.head.appendChild(link)
    }

    makeLink('icon')
    // Add compatibility link for older Chromium variants
    makeLink('shortcut icon')
}

const getBaseHref = (): string => {
    if (originalBaseHref) return originalBaseHref

    // Try to find an existing non-data icon first (set by AppLayout or HTML)
    const existing = Array.from(document.querySelectorAll<HTMLLinkElement>("link[rel*='icon']")).find((l) => {
        const h = l.href || ''
        return h && !h.startsWith('data:image')
    })
    originalBaseHref = existing?.href || '/assets/favicon.svg'
    return originalBaseHref
}

const loadBaseImage = async (src: string): Promise<HTMLImageElement> => {
    if (cachedBaseImage && cachedBaseHref === src) return cachedBaseImage
    if (loadingPromise && cachedBaseHref === src) return loadingPromise

    loadingPromise = new Promise((resolve, reject) => {
        const img = new Image()
        img.onload = () => {
            cachedBaseHref = src
            cachedBaseImage = img
            resolve(img)
        }
        img.onerror = (e) => reject(e)
        img.src = src
    })

    return loadingPromise
}

const drawBadge = (base: HTMLImageElement | null, count: number, pulse = false): string => {
    const size = 64 // Hi-DPI base; browser will scale down
    if (!sharedCanvas) {
        sharedCanvas = document.createElement('canvas')
        sharedCanvas.width = size
        sharedCanvas.height = size
        sharedCtx = sharedCanvas.getContext('2d')
    }
    const canvas = sharedCanvas!
    const ctx = sharedCtx!

    // Draw base icon centered
    ctx.clearRect(0, 0, size, size)
    if (base) {
        try {
            ctx.drawImage(base, 0, 0, size, size)
        } catch {
            // Ignore draw failures; proceed with dot-only
        }
    }

    // Badge parameters (dot-only for clarity across DPIs)
    const radius = 14
    const cx = size - radius - 4
    const cy = radius + 4

    // Optional pulse ring (subtle)
    if (pulse) {
        ctx.beginPath()
        ctx.arc(cx, cy, radius + 6, 0, Math.PI * 2)
        ctx.fillStyle = 'rgba(225, 29, 72, 0.25)' // lobster tone with alpha
        ctx.fill()
    }

    // Badge circle
    ctx.fillStyle = colors.lobster60 || '#e11d48'
    ctx.beginPath()
    ctx.arc(cx, cy, radius, 0, Math.PI * 2)
    ctx.fill()

    // Border ring for contrast
    ctx.lineWidth = 3
    ctx.strokeStyle = colors.primaryWhite || '#ffffff'
    ctx.beginPath()
    ctx.arc(cx, cy, radius, 0, Math.PI * 2)
    ctx.stroke()

    // No numerals; dot-only for legibility

    return canvas.toDataURL('image/png')
}

export const setFaviconBadgeCount = async (count: number): Promise<void> => {
    if (typeof document === 'undefined') return
    const link = ensureFaviconLink()
    const baseHref = getBaseHref()

    if (!count || count <= 0) {
        // Restore base favicon
        applyFaviconHref(baseHref)
        lastDisplayedCount = 0
        return
    }

    let img: HTMLImageElement | null = null
    try {
        img = await loadBaseImage(baseHref)
    } catch {
        img = null
    }
    if (lastDisplayedCount === count && link.href && link.href.startsWith('data:image')) {
        // Already showing this value; skip redraw
        return
    }
    const dataUrl = drawBadge(img, count)
    applyFaviconHref(dataUrl)
    lastDisplayedCount = count
}

export const resetFaviconBadge = (): void => {
    if (typeof document === 'undefined') return
    const baseHref = getBaseHref()
    applyFaviconHref(baseHref)
    lastDisplayedCount = 0
}

// One-shot pulse: briefly show a soft ring behind the badge, then restore
export const pulseFaviconBadge = async (count: number): Promise<void> => {
    if (typeof document === 'undefined') return
    if (!count || count <= 0) return
    const baseHref = getBaseHref()
    const img = await loadBaseImage(baseHref)
    const ringUrl = drawBadge(img, count, true)
    const normalUrl = drawBadge(img, count, false)
    applyFaviconHref(ringUrl)
    setTimeout(() => {
        applyFaviconHref(normalUrl)
        lastDisplayedCount = count
    }, 220)
}
